import React, { useEffect } from 'react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { Container, Form } from '@ahc-digital/component-library';
import { PromptForMissingDatasource } from 'src/shared/PromptForMissingDatasource';
import { SwitchToPreviewMessage } from 'src/shared/SwitchToPreviewMessage';
import { translationClasses } from 'src/shared/TranslationClasses';
import { F01FormProps } from './F01Form.types';
import { useConditionalRendering } from 'src/shared/useConditionalRendering';
import { useCreateMarketingRenderingHame } from 'src/shared/useCreateMarketingRenderingName';

export const Default = (props?: F01FormProps) => {
  const { rendering, params, fields } = props || {};
  const id = params?.RenderingIdentifier;
  const { sitecoreContext } = useSitecoreContext();
  const isEditing = sitecoreContext?.pageEditing ?? false;
  const formioProjectURL = process.env.NEXT_PUBLIC_GLOBAL_FORMIO_PROJECT_URL || '';
  const formioScript = 'https://formio-prod-cdn.azureedge.net/scripts/hse.forms.min.js';
  const { renderLink } = useConditionalRendering();
  const marketingRenderingName = useCreateMarketingRenderingHame(
    props?.rendering?.componentName as string
  );

  useEffect(() => {
    const loadCreateForm = () => {
      return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = formioScript;
        script.type = 'module';
        script.async = true;

        script.onload = () => {
          if (typeof window.createForm === 'function') {
            resolve();
          } else {
            reject(new Error('createForm is not defined on window'));
          }
        };

        script.onerror = () => {
          reject(new Error('Failed to load the createForm script'));
        };

        document.body.appendChild(script);
      });
    };

    loadCreateForm()
      .then(() => {
        // Now it's safe to use window.createForm
        window.createForm(`${fields?.FormName?.value}`, {
          redirect: `${fields?.ThankYouPageUrl?.value?.href}`,
          formProjectUrl: `${formioProjectURL}`,
        });
      })
      .catch((error) => {
        console.error(error);
      });

    // Cleanup script on component unmount
    return () => {
      const script = document.querySelector(`script[src="${formioScript}"]`);
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, [fields?.FormName?.value, fields?.ThankYouPageUrl?.value.href, formioProjectURL]);

  if (!rendering?.dataSource) {
    return <PromptForMissingDatasource rendering={rendering} />;
  }

  if (isEditing) {
    return (
      <SwitchToPreviewMessage componentName={props?.rendering?.componentName}>
        <Container>
          <div className={`component ${params?.styles}`} id={id ? id : undefined}>
            <div className="component-content">
              <p>
                <b>Form Name:</b> <Text field={fields?.FormName} />
              </p>
              <p>
                <b>Thank You Page Url:</b> {renderLink(fields?.ThankYouPageUrl)}
              </p>
            </div>
          </div>
        </Container>
      </SwitchToPreviewMessage>
    );
  }

  return (
    <Form
      id={props?.params?.AnchorLinkTargetName}
      className={translationClasses(props?.params)}
      data-component={marketingRenderingName}
    />
  );
};
